import type { PortalShippingAddress, PortalSubscription } from '@api/customer-portal-api';
import i18n from '@i18n/index';
import { cadenceIntervalFromDays, cadenceLabelFromDays } from './frequencyOffer';
import { recurringPriceDiscount } from './discountOffer';

export const formatAddressLines = (addr: PortalShippingAddress): string[] => {
  const lines: string[] = [];
  if (addr.line1) lines.push(addr.line1);
  if (addr.line2) lines.push(addr.line2);
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
  const regionLine = [cityState, addr.postal_code].filter(Boolean).join(' ').trim();
  if (regionLine) lines.push(regionLine);
  if (addr.country) lines.push(addr.country);
  return lines;
};

export const titleCase = (s: string): string =>
  s
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ');

type FrequencyKey =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'yearly'
  | 'annually';

const FREQUENCY_KEYS: readonly FrequencyKey[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'bimonthly',
  'quarterly',
  'yearly',
  'annually',
];

const getFrequencyKey = (f: string): FrequencyKey | null => {
  const v = (f || '').trim().toLowerCase();
  return (FREQUENCY_KEYS as readonly string[]).includes(v) ? (v as FrequencyKey) : null;
};

export const fmtAmount = (cents: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const fmtFrequency = (f: string) => {
  const key = getFrequencyKey(f);
  if (key) return i18n.t(`frequency.${key}`);
  return f ? titleCase(f) : '—';
};

const fmtFrequencyInterval = (f: string) => {
  const key = getFrequencyKey(f);
  if (key) return i18n.t(`frequencyInterval.${key}`);
  return f ? f.trim().toLowerCase() : '—';
};

// Subscription frequency label. The 8 known keys keep today's labels ("Monthly"); a
// cadence set by a cancellation offer ("every 45 days", "triweekly") reads from
// billing_period_days so it translates; otherwise the title-case fallback.
export const fmtSubscriptionFrequency = (sub: PortalSubscription) =>
  cadenceLabelFromDays(
    i18n.t,
    sub.frequency,
    sub.billing_period_days,
    (f) => getFrequencyKey(f) !== null,
  ) ?? fmtFrequency(sub.frequency);

// The same rule for the interval after a price ("/ month", "/ 45 days").
export const fmtSubscriptionFrequencyInterval = (sub: PortalSubscription) =>
  cadenceIntervalFromDays(
    i18n.t,
    sub.frequency,
    sub.billing_period_days,
    (f) => getFrequencyKey(f) !== null,
  ) ?? fmtFrequencyInterval(sub.frequency);

// Short "which subscription" label for the cancellation modals.
export const subLabel = (sub: PortalSubscription | null): string => {
  if (!sub) return '';
  const name = sub.line_item?.name ?? i18n.t('dashboard.subscriptionFallbackName');
  const cents = recurringPriceDiscount(sub)?.discountedCents ?? sub.amount;
  return `${name} · ${fmtAmount(cents, sub.currency)} ${fmtSubscriptionFrequency(sub)}`;
};

export const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
};

// The subscriptions list sends zone-less UTC times and pause-options sends `…Z` times; read
// both as UTC so a time already carrying an offset keeps it. A date-only string is UTC in JS.
const HAS_ZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const parseUtcDate = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(HAS_ZONE.test(iso) || DATE_ONLY.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The UTC calendar date: what fmtDate already shows for the list's zone-less times, so the
// details page and the pause pop-up agree whichever endpoint a date came from. fmtDate stays
// as is for the cancel flow.
export const fmtUtcDate = (iso?: string | null) => {
  const d = parseUtcDate(iso);
  return d
    ? d.toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : '—';
};
